import type {
  PlanningList,
  CreatePlanningListInput,
  UpdatePlanningListInput,
  ClonePlanningListInput,
  SavePlanningListAsTemplateInput,
  PlanningItem,
  CreatePlanningItemInput,
  UpdatePlanningItemInput,
  CreateItemTaskInput,
  ChecklistItem,
} from '@peles/shared';
import { apiClient } from './client';

// ─── Planning Lists ───────────────────────────────────────────────────────────

export async function getPlanningLists(filters?: {
  eventId?: string;
  isTemplate?: boolean;
  eventTypeId?: string;
}): Promise<PlanningList[]> {
  const params: Record<string, string> = {};
  if (filters?.eventId) params.eventId = filters.eventId;
  if (filters?.isTemplate !== undefined) params.isTemplate = String(filters.isTemplate);
  if (filters?.eventTypeId) params.eventTypeId = filters.eventTypeId;
  const { data } = await apiClient.get<PlanningList[]>('/planning/lists', { params });
  return data;
}

export async function getPlanningList(id: string): Promise<PlanningList> {
  const { data } = await apiClient.get<PlanningList>(`/planning/lists/${id}`);
  return data;
}

export async function createPlanningList(input: CreatePlanningListInput): Promise<PlanningList> {
  const { data } = await apiClient.post<PlanningList>('/planning/lists', input);
  return data;
}

export async function updatePlanningList(
  id: string,
  updates: UpdatePlanningListInput,
): Promise<PlanningList> {
  const { data } = await apiClient.patch<PlanningList>(`/planning/lists/${id}`, updates);
  return data;
}

export async function deletePlanningList(id: string): Promise<void> {
  await apiClient.delete(`/planning/lists/${id}`);
}

export async function clonePlanningList(
  id: string,
  body: ClonePlanningListInput,
): Promise<PlanningList> {
  const { data } = await apiClient.post<PlanningList>(`/planning/lists/${id}/clone`, body);
  return data;
}

export async function savePlanningListAsTemplate(
  id: string,
  body: SavePlanningListAsTemplateInput,
): Promise<PlanningList> {
  const { data } = await apiClient.post<PlanningList>(
    `/planning/lists/${id}/save-as-template`,
    body,
  );
  return data;
}

export async function archivePlanningList(id: string): Promise<PlanningList> {
  const { data } = await apiClient.patch<PlanningList>(`/planning/lists/${id}/archive`, {});
  return data;
}

export async function getPlanningTemplates(filters?: {
  eventTypeId?: string;
  includeArchived?: boolean;
}): Promise<PlanningList[]> {
  const params: Record<string, string> = {};
  if (filters?.eventTypeId) params.eventTypeId = filters.eventTypeId;
  if (filters?.includeArchived !== undefined) {
    params.includeArchived = String(filters.includeArchived);
  }
  const { data } = await apiClient.get<PlanningList[]>('/planning/templates', { params });
  return data;
}

// ─── Planning Items ───────────────────────────────────────────────────────────

export async function addPlanningItem(
  listId: string,
  input: CreatePlanningItemInput,
): Promise<PlanningItem> {
  const { data } = await apiClient.post<PlanningItem>(`/planning/lists/${listId}/items`, input);
  return data;
}

export async function updatePlanningItem(
  itemId: string,
  updates: UpdatePlanningItemInput,
): Promise<PlanningItem> {
  const { data } = await apiClient.patch<PlanningItem>(`/planning/items/${itemId}`, updates);
  return data;
}

export async function deletePlanningItem(itemId: string): Promise<void> {
  await apiClient.delete(`/planning/items/${itemId}`);
}

// ─── An event's one list ─────────────────────────────────────────────────────

/** The event's item list, or null while it has none (it is made with its first item). */
export async function getEventPlanningList(eventId: string): Promise<PlanningList | null> {
  const { data } = await apiClient.get<PlanningList | null>(`/planning/events/${eventId}/list`);
  return data;
}

/** Items onto the event's list — one, a paste, a duplicate — all or none. */
export async function addEventItems(
  eventId: string,
  items: CreatePlanningItemInput[],
): Promise<PlanningItem[]> {
  const { data } = await apiClient.post<PlanningItem[]>(`/planning/events/${eventId}/items`, {
    items,
  });
  return data;
}

/** Several items onto a list (a template's). */
export async function addPlanningItems(
  listId: string,
  items: CreatePlanningItemInput[],
): Promise<PlanningItem[]> {
  const { data } = await apiClient.post<PlanningItem[]>(`/planning/lists/${listId}/items/bulk`, {
    items,
  });
  return data;
}

/** The event's items in a new order — every one of them, once. */
export async function reorderEventItems(eventId: string, itemIds: string[]): Promise<void> {
  await apiClient.put(`/planning/events/${eventId}/item-order`, { itemIds });
}

// ─── An item's tasks ──────────────────────────────────────────────────────────

export async function getItemTasks(itemId: string): Promise<ChecklistItem[]> {
  const { data } = await apiClient.get<ChecklistItem[]>(`/planning/items/${itemId}/tasks`);
  return data;
}

export async function addItemTask(
  itemId: string,
  input: CreateItemTaskInput,
): Promise<ChecklistItem> {
  const { data } = await apiClient.post<ChecklistItem>(`/planning/items/${itemId}/tasks`, input);
  return data;
}
